import { Box, Typography, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Tooltip, TablePagination, TextField, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { Edit, Delete, Visibility, Publish, Event as EventIcon } from '@mui/icons-material';
import { Event, EventCategory, EventStatus } from '../../types';
import { getCategoryLabel, getStatusLabel, getStatusColor, getCategoryColor } from '../../utils/events.utils';

interface EventsTableProps {
  events: Event[];
  loading: boolean;
  total: number;
  page: number;
  limit: number;
  onPageChange: (page: number) => void;
  onEdit: (event: Event) => void;
  onDelete: (id: number) => void;
  onPublish: (id: number) => void;
  onUnpublish: (id: number) => void;
}

export function EventsTable({ events, loading, total, page, limit, onPageChange, onEdit, onDelete, onPublish, onUnpublish }: EventsTableProps) {
  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Дата</TableCell>
            <TableCell>Название</TableCell>
            <TableCell>Категория</TableCell>
            <TableCell>Храм</TableCell>
            <TableCell>Статус</TableCell>
            <TableCell>Действия</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {events.map((event) => (
            <TableRow key={event.id} hover>
              <TableCell>{new Date(event.eventDate).toLocaleDateString('ru-RU')}</TableCell>
              <TableCell>{event.title}</TableCell>
              <TableCell>
                <Chip 
                  label={getCategoryLabel(event.category)} 
                  size="small" 
                  color={getCategoryColor(event.category)}
                  icon={<EventIcon />}
                />
              </TableCell>
              <TableCell>{event.templeId ? `Храм #${event.templeId}` : '-'}</TableCell>
              <TableCell>
                <Chip 
                  label={getStatusLabel(event.status)} 
                  size="small" 
                  color={getStatusColor(event.status)}
                />
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Tooltip title="Просмотр">
                    <IconButton size="small" onClick={() => onEdit(event)}>
                      <Visibility fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Редактировать">
                    <IconButton size="small" onClick={() => onEdit(event)}>
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {event.status === EventStatus.DRAFT && (
                    <Tooltip title="Опубликовать">
                      <IconButton size="small" onClick={() => onPublish(event.id)}>
                        <Publish fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {event.status === EventStatus.PUBLISHED && (
                    <Tooltip title="Снять с публикации">
                      <IconButton size="small" onClick={() => onUnpublish(event.id)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Удалить">
                    <IconButton size="small" onClick={() => onDelete(event.id)} sx={{ color: 'error.main' }}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </TableCell>
            </TableRow>
          ))}
          {events.length === 0 && !loading && (
            <TableRow>
              <TableCell colSpan={6} align="center">
                <Typography color="text.secondary">События не найдены</Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <TablePagination
        component="div"
        count={total}
        page={page - 1}
        onPageChange={(_, newPage) => onPageChange(newPage + 1)}
        rowsPerPage={limit}
        onRowsPerPageChange={() => {}}
        rowsPerPageOptions={[]}
      />
    </TableContainer>
  );
}

interface EventsToolbarProps {
  search: string;
  onSearchChange: (value: string) => void;
  statusFilter: EventStatus | '';
  onStatusFilterChange: (value: EventStatus | '') => void;
  categoryFilter: EventCategory | '';
  onCategoryFilterChange: (value: EventCategory | '') => void;
}

export function EventsToolbar({ search, onSearchChange, statusFilter, onStatusFilterChange, categoryFilter, onCategoryFilterChange }: EventsToolbarProps) {
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }}>
      <TextField
        label="Поиск"
        variant="outlined"
        size="small"
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        sx={{ minWidth: 200 }}
      />
      <FormControl size="small" sx={{ minWidth: 150 }}>
        <InputLabel>Статус</InputLabel>
        <Select
          value={statusFilter}
          label="Статус"
          onChange={(e) => onStatusFilterChange(e.target.value as EventStatus | '')}
        >
          <MenuItem value="">Все</MenuItem>
          <MenuItem value={EventStatus.DRAFT}>Черновик</MenuItem>
          <MenuItem value={EventStatus.PUBLISHED}>Опубликовано</MenuItem>
          <MenuItem value={EventStatus.ARCHIVED}>Архив</MenuItem>
        </Select>
      </FormControl>
      <FormControl size="small" sx={{ minWidth: 150 }}>
        <InputLabel>Категория</InputLabel>
        <Select
          value={categoryFilter}
          label="Категория"
          onChange={(e) => onCategoryFilterChange(e.target.value as EventCategory | '')}
        >
          <MenuItem value="">Все</MenuItem>
          <MenuItem value={EventCategory.LITURGY}>Богослужение</MenuItem>
          <MenuItem value={EventCategory.MEETING}>Встреча</MenuItem>
          <MenuItem value={EventCategory.FEAST}>Праздник</MenuItem>
          <MenuItem value={EventCategory.EDUCATION}>Образовательное</MenuItem>
          <MenuItem value={EventCategory.CHARITY}>Благотворительность</MenuItem>
          <MenuItem value={EventCategory.OTHER}>Другое</MenuItem>
        </Select>
      </FormControl>
      {(search || statusFilter || categoryFilter) && (
        <>
          <Box sx={{ borderLeft: '1px solid #ccc', ml: 1, pl: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Активны фильтры
            </Typography>
          </Box>
        </>
      )}
    </Box>
  );
}
